<template>
    <div class="flex min-h-screen font-sans">
        <!-- Section gauche - Image/Branding -->
        <div class="flex-1 bg-gradient-to-br from-primary to-primary-dark flex items-center justify-center p-10 relative overflow-hidden">
            <!-- Decorative circles -->
            <div class="absolute w-[500px] h-[500px] bg-secondary/10 rounded-full -top-24 -left-24"></div>
            <div class="absolute w-[400px] h-[400px] bg-secondary/5 rounded-full -bottom-24 -right-24"></div>

            <div class="relative z-10 text-center text-white">
                <img
                    src="@/assets/images/sellhub-logo.png"
                    alt="SellHub Logo"
                    class="h-20 w-auto mx-auto mb-8 brightness-0 invert"
                />
                <h2 class="text-4xl font-bold mb-4 leading-tight">
                    Bienvenue sur SellHub
                </h2>
                <p class="text-lg opacity-90 font-light">
                    Gérez votre activité commerciale en toute simplicité
                </p>
            </div>
        </div>

        <!-- Section droite - Formulaire -->
        <div class="flex-1 flex items-center justify-center p-10 bg-white">
            <div class="w-full max-w-[440px]">
                <!-- Header -->
                <div class="mb-10">
                    <h1 class="text-primary text-3xl font-bold mb-2">
                        Connexion
                    </h1>
                    <p class="text-gray-500 text-base">
                        Connectez-vous à votre compte
                    </p>
                </div>

                <!-- Error message -->
                <div
                    v-if="error"
                    class="bg-red-50 text-red-900 px-4 py-3.5 rounded-lg mb-6 text-sm border-l-4 border-red-500"
                >
                    {{ error }}
                </div>

                <!-- Login Form -->
                <form @submit.prevent="handleLogin" class="space-y-6">
                    <!-- Email Input -->
                    <BaseInput
                        id="email"
                        v-model="form.email"
                        type="email"
                        label="Email"
                        placeholder="votre@email.com"
                        :error="validationErrors.email ? validationErrors.email[0] : null"
                        required
                    >
                        <template #icon-left>
                            <BaseIcon name="mail" size="20" color="#64748b" />
                        </template>
                    </BaseInput>

                    <!-- Password Input -->
                    <div>
                        <BaseInput
                            id="password"
                            v-model="form.password"
                            :type="showPassword ? 'text' : 'password'"
                            label="Mot de passe"
                            placeholder="••••••••"
                            :error="validationErrors.password ? validationErrors.password[0] : null"
                            required
                        >
                            <template #icon-left>
                                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24"
                                    fill="none" stroke="#64748b" stroke-width="2" stroke-linecap="round"
                                    stroke-linejoin="round">
                                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                                    <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
                                </svg>
                            </template>
                            <template #icon-right>
                                <button
                                    type="button"
                                    class="text-gray-500 hover:text-primary transition-colors focus:outline-none"
                                    @click="togglePasswordVisibility"
                                >
                                    <BaseIcon
                                        :name="showPassword ? 'eye-off' : 'eye'"
                                        size="20"
                                    />
                                </button>
                            </template>
                        </BaseInput>
                    </div>

                    <!-- Forgot Password -->
                    <div class="text-right">
                        <router-link
                            to="/forgot-password"
                            class="text-primary text-sm font-medium hover:text-secondary transition-colors"
                        >
                            Mot de passe oublié ?
                        </router-link>
                    </div>

                    <!-- Submit Button -->
                    <BaseButton
                        type="submit"
                        variant="primary"
                        size="lg"
                        block
                        :loading="isLoading"
                        class="hover:-translate-y-0.5 active:translate-y-0 transition-transform"
                    >
                        {{ isLoading ? 'Connexion...' : 'Se connecter' }}
                    </BaseButton>
                </form>

                <!-- Sign up link -->
                <!-- <div class="text-center text-gray-500 text-sm mt-6 mb-8">
                    <p>
                        Pas encore de compte ?
                        <router-link
                            :to="{ name: 'register' }"
                            class="text-primary font-semibold hover:text-secondary transition-colors"
                        >
                            Créer un compte
                        </router-link>
                    </p>
                </div> -->

                <!-- Footer -->
                <footer class="text-center text-gray-400 text-xs pt-6 border-t border-gray-200">
                    <p class="mb-2">
                        © {{ new Date().getFullYear() }} SellHub. Tous droits réservés.
                    </p>
                    <div class="flex items-center justify-center text-xs">
                        <span>Powered by </span>
                        <a
                            href="https://digita.sn"
                            target="_blank"
                            class="text-secondary font-semibold ml-1 hover:text-primary transition-colors"
                        >
                            Digita SN
                        </a>
                    </div>
                </footer>
            </div>
        </div>
    </div>
</template>

<script>
import { defineComponent, reactive, ref } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useAuthStore } from '@/store/modules/auth'
import { useToastStore } from '@/store/modules/toastStore'
import physicalInventoryService from '@/services/physicalInventoryService'

export default defineComponent({
    name: 'LoginView',

    setup() {
        const authStore = useAuthStore()
        const toastStore = useToastStore()
        const router = useRouter()
        const route = useRoute()

        const form = reactive({
            email: '',
            password: ''
        })

        const isLoading = ref(false)
        const error = ref(null)
        const validationErrors = ref({})
        const showPassword = ref(false)

        const togglePasswordVisibility = () => {
            showPassword.value = !showPassword.value
        }

        const isMobileDevice = () => {
            return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
                   window.innerWidth <= 768
        }

        const checkOngoingInventory = async () => {
            try {
                const response = await physicalInventoryService.getInventories({
                    status: 'in_progress',
                    per_page: 1,
                    page: 1
                })

                return response.data && response.data.length > 0
            } catch (error) {
                console.error('Erreur lors de la vérification de l\'inventaire:', error)
                return false
            }
        }

        const handleLogin = async () => {
            isLoading.value = true
            error.value = null
            validationErrors.value = {}

            try {
                await authStore.login({
                    email: form.email,
                    phone: form.email,
                    password: form.password
                })
                console.log('isMobileDevice():', isMobileDevice())
                if (isMobileDevice()) {
                    const hasOngoingInventory = await checkOngoingInventory()
                    console.log('hasOngoingInventory():', hasOngoingInventory)
                    if (hasOngoingInventory) {
                        router.push({ name: 'doing-inventory' })
                        return
                    }
                }
                console.log('route.query.redirect:', route.query.redirect)
                const redirectPath = route.query.redirect || '/dashboard'
                router.push(redirectPath)
            } catch (err) {
                console.error('Login error:', err)

                if (err.response && err.response.status === 422) {
                    validationErrors.value = err.response.data.errors || {}
                    error.value = err.response.data.message || 'Erreur de validation'
                }
                else if (err.response && err.response.status === 401) {
                    error.value = 'Email ou mot de passe incorrect'
                }
                else if (err.response && err.response.data && err.response.data.verification_needed) {
                    error.value = "Votre email n'est pas vérifié. Veuillez vérifier votre boîte mail."

                    toastStore.addToast({
                        type: 'info',
                        message: "Vous pouvez demander un nouvel email de vérification",
                        duration: 5000
                    })
                }
                else {
                    error.value = err.response?.data?.message || 'Une erreur est survenue lors de la connexion'
                }
            } finally {
                isLoading.value = false
            }
        }

        return {
            form,
            isLoading,
            error,
            validationErrors,
            showPassword,
            togglePasswordVisibility,
            handleLogin
        }
    }
})
</script>

<style scoped>
/* Responsive design for smaller screens */
@media (max-width: 968px) {
    .flex {
        flex-direction: column;
    }
}
</style>
